import {
  AlignmentMode,
  LayoutTemplate,
  ProjectCalculation,
  ProjectState,
  SurfaceCalculation,
  SurfaceId,
  TileCategory,
  TileData,
  TilePlacement,
} from '../types';
import { getLayoutPattern, getLayoutTemplate } from '../layouts/templates';
import { getFriezeMix } from '../layouts/friezeMixes';

export const SURFACE_LABELS: Record<SurfaceId, string> = {
  front: 'Передняя стена',
  left: 'Левая стена',
  right: 'Правая стена',
  back: 'Задняя стена',
  floor: 'Пол',
};

export const SURFACE_IDS: SurfaceId[] = ['front', 'left', 'right', 'back', 'floor'];

export const getSurfaceDimensions = (
  project: Pick<ProjectState, 'room'>,
  surfaceId: SurfaceId,
): { width: number; height: number } => {
  if (surfaceId === 'floor') {
    return { width: project.room.width, height: project.room.length };
  }

  return {
    width: surfaceId === 'left' || surfaceId === 'right' ? project.room.length : project.room.width,
    height: project.room.height,
  };
};

const alignmentOffset = (
  surfaceSize: number,
  tileSize: number,
  grout: number,
  alignment: AlignmentMode,
  customOffset: number,
  minCutSize: number,
  symmetry: boolean,
): number => {
  const pitch = tileSize + grout;
  const remainder = (surfaceSize + grout) % pitch;
  const centeredEdge = remainder === 0
    ? 0
    : remainder / 2 < minCutSize
      ? (remainder + pitch) / 2
      : remainder / 2;

  if (symmetry || alignment === 'center') return centeredEdge === 0 ? 0 : centeredEdge - tileSize;
  if (alignment === 'left') {
    return remainder > 0 && remainder < minCutSize ? minCutSize - tileSize : 0;
  }
  if (alignment === 'right') {
    return remainder > 0 && remainder < minCutSize ? remainder - minCutSize : remainder - tileSize;
  }
  if (alignment === 'custom') return customOffset % pitch;
  return remainder >= minCutSize ? 0 : centeredEdge - tileSize;
};

const intersectionArea = (
  placement: TilePlacement,
  opening: ProjectState['objects'][number],
  surfaceHeight: number,
): number => {
  const openingTop = surfaceHeight - opening.y - opening.height;
  const width = Math.max(
    0,
    Math.min(placement.x + placement.width, opening.x + opening.width) -
      Math.max(placement.x, opening.x),
  );
  const height = Math.max(
    0,
    Math.min(placement.y + placement.height, openingTop + opening.height) -
      Math.max(placement.y, openingTop),
  );
  return width * height;
};

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Point {
  x: number;
  y: number;
}

const zoneBounds = (
  zone: LayoutTemplate['zones'][number],
  width: number,
  height: number,
): Bounds => zone.axis === 'vertical'
  ? { x: zone.start * width, y: 0, width: (zone.end - zone.start) * width, height }
  : {
      x: 0,
      y: (1 - zone.end) * height,
      width,
      height: (zone.end - zone.start) * height,
    };

const rotatedDimensions = (tile: TileData) => (
  tile.rotation === 90 || tile.rotation === 270
    ? { width: tile.tileHeight, height: tile.tileWidth }
    : { width: tile.tileWidth, height: tile.tileHeight }
);

const clipByHalfPlane = (
  polygon: Point[],
  inside: (point: Point) => boolean,
  intersect: (start: Point, end: Point) => Point,
): Point[] => {
  const result: Point[] = [];
  polygon.forEach((current, index) => {
    const previous = polygon[(index + polygon.length - 1) % polygon.length];
    const currentInside = inside(current);
    const previousInside = inside(previous);
    if (currentInside) {
      if (!previousInside) result.push(intersect(previous, current));
      result.push(current);
    } else if (previousInside) {
      result.push(intersect(previous, current));
    }
  });
  return result;
};

/** Площадь пересечения выпуклого многоугольника с прямоугольником поверхности (мм²) */
export const polygonIntersectionArea = (polygon: Point[], bounds: Bounds): number => {
  const horizontal = (value: number, start: Point, end: Point): Point => ({
    x: value,
    y: start.y + (end.y - start.y) * ((value - start.x) / (end.x - start.x)),
  });
  const vertical = (value: number, start: Point, end: Point): Point => ({
    y: value,
    x: start.x + (end.x - start.x) * ((value - start.y) / (end.y - start.y)),
  });

  let clipped = polygon;
  clipped = clipByHalfPlane(clipped, (point) => point.x >= bounds.x, (a, b) => horizontal(bounds.x, a, b));
  clipped = clipByHalfPlane(clipped, (point) => point.x <= bounds.x + bounds.width, (a, b) => horizontal(bounds.x + bounds.width, a, b));
  clipped = clipByHalfPlane(clipped, (point) => point.y >= bounds.y, (a, b) => vertical(bounds.y, a, b));
  clipped = clipByHalfPlane(clipped, (point) => point.y <= bounds.y + bounds.height, (a, b) => vertical(bounds.y + bounds.height, a, b));
  if (clipped.length < 3) return 0;

  let area = 0;
  clipped.forEach((point, index) => {
    const next = clipped[(index + 1) % clipped.length];
    area += point.x * next.y - next.x * point.y;
  });
  return Math.abs(area) / 2;
};

export const rotatePoint = (point: Point, center: Point, degrees: number): Point => {
  const radians = (degrees * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cosine - dy * sine,
    y: center.y + dx * sine + dy * cosine,
  };
};

const rectangleCorners = (
  x: number,
  y: number,
  width: number,
  height: number,
  center: Point,
  degrees: number,
): Point[] => [
  { x, y },
  { x: x + width, y },
  { x: x + width, y: y + height },
  { x, y: y + height },
].map((corner) => (degrees ? rotatePoint(corner, center, degrees) : corner));

interface CellStyle {
  category: TileCategory;
  /** Дополнительный поворот картинки плитки, кратный 180° (не меняет габарит) */
  extraRotation: number;
  mirror: boolean;
}

const defaultCell = (tile: TileData): CellStyle => ({
  category: tile.category,
  extraRotation: 0,
  mirror: false,
});

/** Комбинация фриза: категория плитки идёт по кругу вдоль ряда */
const mixCellResolver = (cycle: TileCategory[]) =>
  (row: number, column: number): CellStyle => ({
    category: cycle[((column % cycle.length) + cycle.length) % cycle.length],
    extraRotation: 0,
    mirror: false,
  });

interface GridOptions {
  offset: 0 | 0.33 | 0.5;
  /** Ступени: ряды идут парами со сдвигом на половину плитки */
  pairs?: boolean;
  /** Абсолютный сдвиг строки, переопределяет offset */
  resolveRowOffset?: (row: number, size: { width: number; height: number }) => number;
  resolveCell?: (row: number, column: number, tile: TileData) => CellStyle;
}

const createGridPlacements = (
  surfaceId: SurfaceId,
  zoneId: string,
  bounds: Bounds,
  tile: TileData,
  project: ProjectState,
  options: GridOptions,
  openings: ProjectState['objects'],
  surfaceHeight: number,
): TilePlacement[] => {
  const settings = project.surfaces[surfaceId];
  const grout = project.grout.width;
  const size = rotatedDimensions(tile);
  const startX = bounds.x + alignmentOffset(
    bounds.width,
    size.width,
    grout,
    settings.alignment,
    settings.customOffset,
    settings.minCutSize,
    settings.symmetry,
  );
  const startY = bounds.y + alignmentOffset(
    bounds.height,
    size.height,
    grout,
    settings.alignment,
    settings.customOffset,
    settings.minCutSize,
    settings.symmetry,
  );
  const placements: TilePlacement[] = [];
  let row = 0;

  for (let y = startY; y < bounds.y + bounds.height; y += size.height + grout) {
    const rowOffset = options.resolveRowOffset
      ? options.resolveRowOffset(row, size)
      : options.offset
        ? (options.pairs
            ? Math.floor(row / 2) % 2
            : row % Math.round(1 / options.offset)) * size.width * options.offset
        : 0;
    let column = 0;

    for (let x = startX - rowOffset; x < bounds.x + bounds.width; x += size.width + grout) {
      const visibleX = Math.max(bounds.x, x);
      const visibleY = Math.max(bounds.y, y);
      const visibleWidth = Math.min(x + size.width, bounds.x + bounds.width) - visibleX;
      const visibleHeight = Math.min(y + size.height, bounds.y + bounds.height) - visibleY;

      if (visibleWidth > 0 && visibleHeight > 0) {
        const style = options.resolveCell
          ? options.resolveCell(row, column, tile)
          : defaultCell(tile);
        const placement: TilePlacement = {
          id: `${surfaceId}-${zoneId}-${row}-${column}`,
          category: style.category,
          x: visibleX,
          y: visibleY,
          width: visibleWidth,
          height: visibleHeight,
          sourceX: Math.max(0, visibleX - x),
          sourceY: Math.max(0, visibleY - y),
          sourceWidth: visibleWidth,
          sourceHeight: visibleHeight,
          rotation: (tile.rotation + style.extraRotation) % 360,
          diagonal: false,
          coveredArea: visibleWidth * visibleHeight,
          whole: visibleWidth >= size.width * 0.999 && visibleHeight >= size.height * 0.999,
          row,
          column,
          mirror: style.mirror,
        };
        const removedArea = Math.min(
          placement.coveredArea,
          openings.reduce(
            (sum, opening) => sum + intersectionArea(placement, opening, surfaceHeight),
            0,
          ),
        );
        placement.coveredArea -= removedArea;
        if (removedArea > 0) placement.whole = false;
        if (placement.coveredArea > 0) placements.push(placement);
      }
      column += 1;
    }
    row += 1;
  }

  return placements;
};

const createDiagonalPlacements = (
  surfaceId: SurfaceId,
  zoneId: string,
  bounds: Bounds,
  tile: TileData,
  project: ProjectState,
): TilePlacement[] => {
  const size = rotatedDimensions(tile);
  const grout = project.grout.width;
  const settings = project.surfaces[surfaceId];
  const cosine = Math.SQRT1_2;
  const boundingSize = (size.width + size.height) * cosine;
  const centerX = settings.symmetry || settings.alignment === 'center'
    ? bounds.x + bounds.width / 2
    : bounds.x + boundingSize / 2 + alignmentOffset(
        bounds.width,
        boundingSize,
        grout,
        settings.alignment,
        settings.customOffset,
        settings.minCutSize,
        false,
      );
  const centerY = settings.symmetry || settings.alignment === 'center'
    ? bounds.y + bounds.height / 2
    : bounds.y + boundingSize / 2 + alignmentOffset(
        bounds.height,
        boundingSize,
        grout,
        settings.alignment,
        settings.customOffset,
        settings.minCutSize,
        false,
      );
  const reach = Math.ceil(
    (bounds.width + bounds.height) / Math.min(size.width + grout, size.height + grout),
  ) + 2;
  const placements: TilePlacement[] = [];

  for (let row = -reach; row <= reach; row += 1) {
    for (let column = -reach; column <= reach; column += 1) {
      const tileCenterX = centerX +
        column * (size.width + grout) * cosine -
        row * (size.height + grout) * cosine;
      const tileCenterY = centerY +
        column * (size.width + grout) * cosine +
        row * (size.height + grout) * cosine;
      const boundingHalf = (size.width + size.height) * cosine / 2;
      const minX = tileCenterX - boundingHalf;
      const maxX = tileCenterX + boundingHalf;
      const minY = tileCenterY - boundingHalf;
      const maxY = tileCenterY + boundingHalf;
      const clippedWidth = Math.max(
        0,
        Math.min(maxX, bounds.x + bounds.width) - Math.max(minX, bounds.x),
      );
      const clippedHeight = Math.max(
        0,
        Math.min(maxY, bounds.y + bounds.height) - Math.max(minY, bounds.y),
      );

      if (clippedWidth === 0 || clippedHeight === 0) continue;
      const whole =
        minX >= bounds.x &&
        maxX <= bounds.x + bounds.width &&
        minY >= bounds.y &&
        maxY <= bounds.y + bounds.height;
      placements.push({
        id: `${surfaceId}-${zoneId}-${row}-${column}`,
        category: tile.category,
        x: tileCenterX - size.width / 2,
        y: tileCenterY - size.height / 2,
        width: size.width,
        height: size.height,
        sourceX: 0,
        sourceY: 0,
        sourceWidth: size.width,
        sourceHeight: size.height,
        rotation: 45 + tile.rotation,
        diagonal: true,
        coveredArea: Math.min(size.width * size.height, clippedWidth * clippedHeight / 2),
        whole,
        row: row + reach,
        column: column + reach,
      });
    }
  }

  const coverageRatio = size.width * size.height /
    ((size.width + grout) * (size.height + grout));
  const targetArea = bounds.width * bounds.height * coverageRatio;
  const measuredArea = placements.reduce((sum, placement) => sum + placement.coveredArea, 0);
  const scale = measuredArea > 0 ? targetArea / measuredArea : 0;
  placements.forEach((placement) => {
    placement.coveredArea *= scale;
  });
  return placements;
};

/**
 * Ёлочка (herringbone). Геометрия строится на решётке классической ёлочки:
 * пара «горизонтальная + вертикальная плитка» повторяется по векторам (2u, 2u) и (-u, u),
 * где u = короткая сторона плитки + шов. Вся решётка повёрнута на 45°.
 */
const createHerringbonePlacements = (
  surfaceId: SurfaceId,
  zoneId: string,
  bounds: Bounds,
  tile: TileData,
  project: ProjectState,
): TilePlacement[] => {
  const grout = Math.max(0, Math.min(project.grout.width, 20));
  const short = Math.min(tile.tileWidth, tile.tileHeight);
  const unit = short + grout;
  const cellLong = 2 * unit;
  const cellShort = unit;
  const drawLong = Math.max(unit, cellLong - grout);
  const drawShort = Math.max(unit, cellShort - grout);
  const center: Point = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
  const radius = Math.hypot(bounds.width, bounds.height) / 2 + cellLong * 2;
  const rangeI = Math.ceil(radius / (2 * Math.SQRT2 * unit)) + 2;
  const rangeJ = Math.ceil(radius / (Math.SQRT2 * unit)) + 2;
  const materialLongIsVertical = tile.tileHeight > tile.tileWidth;
  const placements: TilePlacement[] = [];

  for (let i = -rangeI; i <= rangeI; i += 1) {
    for (let j = -rangeJ; j <= rangeJ; j += 1) {
      const baseX = center.x + (2 * unit * i - unit * j);
      const baseY = center.y + (2 * unit * i + unit * j);
      const cells = [
        { x: baseX, y: baseY, width: drawLong, height: drawShort, angle: 45 },
        { x: baseX + unit, y: baseY + unit, width: drawShort, height: drawLong, angle: 135 },
      ];

      cells.forEach((cell, cellIndex) => {
        const tileCenter: Point = {
          x: cell.x + cell.width / 2,
          y: cell.y + cell.height / 2,
        };
        const corners = rectangleCorners(
          cell.x,
          cell.y,
          cell.width,
          cell.height,
          tileCenter,
          cell.angle,
        );
        const coveredArea = polygonIntersectionArea(corners, bounds);
        if (coveredArea <= 0) return;

        const whole = corners.every((corner) =>
          corner.x >= bounds.x - 0.01 &&
          corner.x <= bounds.x + bounds.width + 0.01 &&
          corner.y >= bounds.y - 0.01 &&
          corner.y <= bounds.y + bounds.height + 0.01);
        const rotation = (cell.angle + (materialLongIsVertical ? 90 : 0) + tile.rotation) % 360;

        placements.push({
          id: `${surfaceId}-${zoneId}-hb-${i}-${j}-${cellIndex}`,
          category: tile.category,
          x: tileCenter.x - cell.width / 2,
          y: tileCenter.y - cell.height / 2,
          width: cell.width,
          height: cell.height,
          sourceX: 0,
          sourceY: 0,
          sourceWidth: cell.width,
          sourceHeight: cell.height,
          rotation,
          diagonal: true,
          coveredArea,
          whole,
          row: i + rangeI,
          column: j + rangeJ,
        });
      });
    }
  }

  return placements;
};

/** Модульная раскладка: модуль 3×3, линии декора и медальон по центру модуля */
const createModularPlacements = (
  surfaceId: SurfaceId,
  zoneId: string,
  bounds: Bounds,
  tile: TileData,
  project: ProjectState,
  openings: ProjectState['objects'],
  surfaceHeight: number,
): TilePlacement[] => createGridPlacements(
  surfaceId,
  zoneId,
  bounds,
  tile,
  project,
  {
    offset: 0,
    resolveCell: (row, column, base): CellStyle => {
      const decorTile = project.tiles.decor ?? project.tiles.border;
      const decorRow = row % 3 === 1;
      const decorColumn = column % 3 === 1;
      if (!decorTile) return defaultCell(base);
      if (decorRow && decorColumn) {
        return { category: decorTile.category, extraRotation: 180, mirror: false };
      }
      if (decorRow || decorColumn) {
        return { category: decorTile.category, extraRotation: 0, mirror: false };
      }
      return defaultCell(base);
    },
  },
  openings,
  surfaceHeight,
);

/**
 * Калейдоскоп: сетка выровнена по центру поверхности, узор зеркалится в четыре стороны.
 * Каноническая плитка задаёт поворот и отражение для всех четырёх копий.
 */
const createKaleidoscopePlacements = (
  surfaceId: SurfaceId,
  zoneId: string,
  bounds: Bounds,
  tile: TileData,
  project: ProjectState,
  openings: ProjectState['objects'],
  surfaceHeight: number,
): TilePlacement[] => {
  const grout = project.grout.width;
  const size = rotatedDimensions(tile);
  const pitchX = size.width + grout;
  const pitchY = size.height + grout;
  const center: Point = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
  const columns = Math.ceil(bounds.width / pitchX / 2) + 1;
  const rows = Math.ceil(bounds.height / pitchY / 2) + 1;
  const decorTile = project.tiles.decor ?? project.tiles.border;
  const placements: TilePlacement[] = [];

  for (let i = -columns; i < columns; i += 1) {
    for (let j = -rows; j < rows; j += 1) {
      const x = center.x + (i + 0.5) * pitchX - size.width / 2;
      const y = center.y + (j + 0.5) * pitchY - size.height / 2;
      const visibleX = Math.max(bounds.x, x);
      const visibleY = Math.max(bounds.y, y);
      const visibleWidth = Math.min(x + size.width, bounds.x + bounds.width) - visibleX;
      const visibleHeight = Math.min(y + size.height, bounds.y + bounds.height) - visibleY;
      if (visibleWidth <= 0 || visibleHeight <= 0) continue;

      // Канонический (левый верхний) квадрант: ringX/ringY — расстояние от центра в плитках
      const ringX = Math.min(-i - 1, i);
      const ringY = Math.min(-j - 1, j);
      const onDecorLine = -ringX - 1 >= 0 && ((-ringX - 1) % 3 === 0 || (-ringY - 1) % 3 === 0);
      const category = decorTile && onDecorLine ? decorTile.category : tile.category;
      const mirrorH = i <= -i - 1; // левая половина — горизонтальный отрад вертикальной оси
      const mirrorV = j <= -j - 1; // верхняя половина — вертикальный отрад горизонтальной оси
      const extraRotation = (Math.abs(ringX) + Math.abs(ringY)) % 2 === 0 ? 180 : 0;

      const placement: TilePlacement = {
        id: `${surfaceId}-${zoneId}-kal-${i}-${j}`,
        category,
        x: visibleX,
        y: visibleY,
        width: visibleWidth,
        height: visibleHeight,
        sourceX: Math.max(0, visibleX - x),
        sourceY: Math.max(0, visibleY - y),
        sourceWidth: visibleWidth,
        sourceHeight: visibleHeight,
        rotation: (tile.rotation + extraRotation) % 360,
        diagonal: false,
        coveredArea: visibleWidth * visibleHeight,
        whole: visibleWidth >= size.width * 0.999 && visibleHeight >= size.height * 0.999,
        row: j + rows,
        column: i + columns,
        mirror: mirrorH,
        mirrorY: mirrorV,
      };
      const removedArea = Math.min(
        placement.coveredArea,
        openings.reduce((sum, opening) => sum + intersectionArea(placement, opening, surfaceHeight), 0),
      );
      placement.coveredArea -= removedArea;
      if (removedArea > 0) placement.whole = false;
      if (placement.coveredArea > 0) placements.push(placement);
    }
  }

  return placements;
};

const emptySurface = (dimensions: { width: number; height: number }): SurfaceCalculation => ({
  ...dimensions,
  area: dimensions.width * dimensions.height,
  wholeTiles: 0,
  cutTiles: 0,
  purchasedTiles: 0,
  coveredArea: 0,
  wasteArea: 0,
  tiles: [],
});

export const calculateSurface = (
  project: ProjectState,
  surfaceId: SurfaceId,
): SurfaceCalculation => {
  const dimensions = getSurfaceDimensions(project, surfaceId);
  const settings = project.surfaces[surfaceId];
  const template = getLayoutTemplate(settings.templateId);
  const pattern = getLayoutPattern(template);
  const fallbackCategory: TileCategory = surfaceId === 'floor' ? 'floor' : 'lightWall';
  const baseTile = project.tiles[fallbackCategory] ??
    template.zones.map((zone) => project.tiles[zone.category]).find((tile) => tile !== null);
  const openings = project.objects
    .filter((object) => object.surfaceId === surfaceId)
    .map((object) => ({
      ...object,
      // Обрезаем объект по границам стены: дверь шире стены не вылезает за раскладку
      width: Math.min(object.width, dimensions.width),
      height: Math.min(object.height, dimensions.height),
      x: Math.min(Math.max(0, object.x), Math.max(0, dimensions.width - Math.min(object.width, dimensions.width))),
      y: Math.min(Math.max(0, object.y), Math.max(0, dimensions.height - Math.min(object.height, dimensions.height))),
    }));

  if (!baseTile) {
    return emptySurface(dimensions);
  }

  const placements: TilePlacement[] = [];
  const friezeMixCycle = getFriezeMix(settings.friezeMix).cycle;

  template.zones.forEach((zone) => {
    const cycle = zone.mix ? friezeMixCycle : null;
    const tile = project.tiles[cycle ? cycle[0] : zone.category] ?? baseTile;
    const bounds = zoneBounds(zone, dimensions.width, dimensions.height);
    if (pattern === 'herringbone') {
      placements.push(...createHerringbonePlacements(surfaceId, zone.id, bounds, tile, project));
      return;
    }
    if (pattern === 'kaleidoscope') {
      placements.push(...createKaleidoscopePlacements(
        surfaceId,
        zone.id,
        bounds,
        tile,
        project,
        openings,
        dimensions.height,
      ));
      return;
    }
    if (pattern === 'modular') {
      placements.push(...createModularPlacements(
        surfaceId,
        zone.id,
        bounds,
        tile,
        project,
        openings,
        dimensions.height,
      ));
      return;
    }
    if (pattern === 'diagonal') {
      placements.push(...createDiagonalPlacements(surfaceId, zone.id, bounds, tile, project));
      return;
    }
    placements.push(...createGridPlacements(
      surfaceId,
      zone.id,
      bounds,
      tile,
      project,
      {
        offset: template.offset,
        pairs: pattern === 'stairs',
        resolveRowOffset: pattern === 'stairs'
          ? (row, size) => Math.floor(row / 2) * (size.width / 2)
          : undefined,
        resolveCell: cycle ? mixCellResolver(cycle) : undefined,
      },
      openings,
      dimensions.height,
    ));
  });

  const coveredArea = placements.reduce((sum, tile) => sum + tile.coveredArea, 0);
  const categories = new Set(placements.map((placement) => placement.category));
  const categoryArea = (category: TileCategory) => placements
    .filter((placement) => placement.category === category)
    .reduce((sum, placement) => sum + placement.coveredArea, 0);
  const purchasedTiles = [...categories].reduce((sum, category) => {
    const area = categoryArea(category);
    const tile = project.tiles[category];
    // Ёлочка укладывается ячейками 2:1 — количество плиток считаем по площади материала
    if (pattern === 'herringbone' && tile) {
      const tileArea = Math.max(1, tile.tileWidth * tile.tileHeight);
      return sum + Math.ceil(area / tileArea * (1 + project.wastePercent / 100));
    }
    const count = placements.filter((placement) => placement.category === category).length;
    return sum + Math.ceil(count * (1 + project.wastePercent / 100));
  }, 0);
  const purchasedArea = [...categories].reduce((sum, category) => {
    const tile = project.tiles[category];
    if (!tile) return sum;
    const area = categoryArea(category);
    if (pattern === 'herringbone') {
      const tileArea = Math.max(1, tile.tileWidth * tile.tileHeight);
      return sum + Math.ceil(area / tileArea * (1 + project.wastePercent / 100)) * tileArea;
    }
    const count = placements.filter((placement) => placement.category === category).length;
    return sum + Math.ceil(count * (1 + project.wastePercent / 100)) * tile.tileWidth * tile.tileHeight;
  }, 0);
  const wholeTiles = placements.filter((tile) => tile.whole).length;
  const cutTiles = placements.length - wholeTiles;

  return {
    ...dimensions,
    area: dimensions.width * dimensions.height,
    wholeTiles,
    cutTiles,
    purchasedTiles,
    coveredArea,
    wasteArea: Math.max(0, purchasedArea - coveredArea),
    tiles: placements,
  };
};

export const calculateProject = (
  project: ProjectState,
  surfaceId: SurfaceId = project.selectedSurface,
): ProjectCalculation => {
  const surface = calculateSurface(project, surfaceId);
  const requiredArea = surface.coveredArea * (1 + project.wastePercent / 100) / 1_000_000;
  const totalPrice = surface.tiles.reduce((sum, placement) => {
    const tile = project.tiles[placement.category];
    return sum + placement.coveredArea / 1_000_000 * (1 + project.wastePercent / 100) * (tile?.price ?? 0);
  }, 0);

  return {
    surface,
    requiredArea,
    totalPrice,
  };
};

/** Расчёт по всем пяти поверхностям помещения */
export const calculateSurfaces = (
  project: ProjectState,
): Array<{ surfaceId: SurfaceId; calculation: SurfaceCalculation; template: LayoutTemplate }> =>
  SURFACE_IDS.map((surfaceId) => ({
    surfaceId,
    calculation: calculateSurface(project, surfaceId),
    template: getLayoutTemplate(project.surfaces[surfaceId].templateId),
  }));

/**
 * Оценка количества плиток в раскладке — используется сервером, чтобы отклонить
 * заведомо неподъёмные проекты до расчёта.
 */
export const estimatePlacementCount = (project: ProjectState, surfaceId: SurfaceId): number => {
  const settings = project.surfaces[surfaceId];
  const template = getLayoutTemplate(settings.templateId);
  const pattern = getLayoutPattern(template);
  const width = surfaceId === 'left' || surfaceId === 'right' ? project.room.length : project.room.width;
  const height = surfaceId === 'floor' ? project.room.length : project.room.height;
  const grout = project.grout.width;
  let total = 0;

  template.zones.forEach((zone) => {
    const tile = project.tiles[zone.category];
    if (!tile) return;
    const rotated = tile.rotation === 90 || tile.rotation === 270;
    const tileWidth = rotated ? tile.tileHeight : tile.tileWidth;
    const tileHeight = rotated ? tile.tileWidth : tile.tileHeight;
    const zoneWidth = zone.axis === 'vertical' ? width * (zone.end - zone.start) : width;
    const zoneHeight = zone.axis === 'horizontal' ? height * (zone.end - zone.start) : height;

    if (pattern === 'herringbone') {
      const short = Math.min(tileWidth, tileHeight) + grout;
      const diagonal = Math.hypot(zoneWidth, zoneHeight);
      // решётка ёлочки: две плитки на узел с шагом 4·short², берём запас ×3
      const cells = Math.PI * diagonal * diagonal / (8 * short * short);
      total += Math.ceil(cells * 3) + 16;
    } else if (pattern === 'diagonal') {
      // ромбы диагональной укладки: площадь ромба = (шагX × шагY) / 2
      const pitchX = Math.max(10, tileWidth + grout);
      const pitchY = Math.max(10, tileHeight + grout);
      total += Math.ceil(2 * zoneWidth * zoneHeight / (pitchX * pitchY) * 3) + 16;
    } else {
      total +=
        (Math.ceil(zoneWidth / (tileWidth + grout)) + 3) *
        (Math.ceil(zoneHeight / (tileHeight + grout)) + 3);
    }
  });

  return total;
};
